// Cycle-16 composer focus-state frame. Wraps the composer surface with
// the design-system-defined idle / focused / recording strokes from the
// `Chat Composer` Figma frame (`2804:26441`). Pure presentational
// container — no state, no IO.
//
// Visual reference: Figma `2804:26707` (the "Search" input pill inside
// `2804:26702`):
//   - 40 tall (collapsed)
//   - pill radius 1000
//   - paddings L10 / R10
//   - fill `#222429` (dark) / `surface.secondary` (light)
// The `tone` discriminator drives the stroke:
//   - idle      → hairline `border.secondary`
//   - focused   → 1 px `accent.indigo` (`#537EFF`)
//   - recording → 1 px `accent.rose`

import React from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { tokens } from '../../design-system/tokens';
import type { ComposerFocusFrameTone } from './ComposerFocusFrameTone';

export { COMPOSER_FOCUS_FRAME_TONES, type ComposerFocusFrameTone } from './ComposerFocusFrameTone';

export interface ComposerFocusFrameProps {
  tone: ComposerFocusFrameTone;
  children: React.ReactNode;
  testID?: string | undefined;
}

export const ComposerFocusFrame = React.memo<ComposerFocusFrameProps>(
  ({ tone, children, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const strokeColor =
      tone === 'recording'
        ? colors.accent.rose
        : tone === 'focused'
          ? colors.accent.indigo
          : colors.border.secondary;

    // Figma `2804:26707`: dark fill `#222429`, light `surface.secondary`.
    // `colors.surface.secondary` resolves to `#2C2C2E` on dark via the
    // semantic token map; we use the literal `#222429` for pixel-parity
    // with the Figma swatch.
    const backgroundColor =
      colorScheme === 'dark' ? '#222429' : colors.surface.secondary;
    const containerStyle: ViewStyle[] = [
      styles.frame,
      {
        backgroundColor,
        borderColor: strokeColor,
        borderWidth: tone === 'idle' ? StyleSheet.hairlineWidth : 1,
      },
    ];

    return (
      <View style={containerStyle} testID={testID} accessibilityState={{ busy: tone === 'recording' }}>
        {children}
      </View>
    );
  },
);

ComposerFocusFrame.displayName = 'ComposerFocusFrame';

const styles = StyleSheet.create({
  // Figma `2804:26707`: paddings L10 R10, no vertical padding because
  // the inner row hugs the 40-tall pill. Pill radius via `tokens.radius.pill`.
  frame: {
    paddingHorizontal: 10,
    paddingVertical: 0,
    minHeight: 40,
    borderRadius: tokens.radius.pill,
    justifyContent: 'center',
  },
});
